/**
 * Parent Household Profile Page
 *
 * Provides a user-friendly interface for parents to view and edit their household information.
 * Unlike the admin data table view, this presents a clean form-based layout suitable for parents.
 */

import { Field } from "@/components/atoms/Field"
import { useUpdateHousehold } from "@/hooks/api/useHouseholds"
import { useMyHousehold } from "@/hooks/api/useMyHousehold"
import {
  type HouseholdUpdateInput,
  householdUpdateSchema,
} from "@/types/household"
import {
  Badge,
  Box,
  Button,
  Card,
  Container,
  Flex,
  HStack,
  Heading,
  Separator,
  Spinner,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react"
import { Input, Switch, Textarea } from "@chakra-ui/react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { Controller, useForm } from "react-hook-form"

export default function ParentHouseholdProfile() {
  const navigate = useNavigate()
  const [isEditing, setIsEditing] = useState(false)

  const { data: household, isLoading, error } = useMyHousehold()
  const updateMutation = useUpdateHousehold()

  const form = useForm<HouseholdUpdateInput>({
    resolver: zodResolver(householdUpdateSchema),
    defaultValues: household || {},
  })

  // Reset form when household data changes
  useEffect(() => {
    if (household && !isEditing) {
      form.reset(household)
    }
  }, [household, isEditing, form])

  const handleSave = async (values: HouseholdUpdateInput) => {
    if (!household) return

    try {
      await updateMutation.mutateAsync({
        householdId: household.id,
        householdData: values,
      })
      setIsEditing(false)
      form.reset(values)
    } catch (error) {
      // Error is handled by the mutation
      console.error("Failed to update household:", error)
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
    form.reset(household || {})
  }

  if (isLoading) {
    return (
      <Container
        maxW="container.md"
        py={{ base: 4, md: 8 }}
        px={{ base: 2, md: 4 }}
      >
        <VStack gap={4}>
          <Spinner size="xl" />
          <Text>Loading your household information...</Text>
        </VStack>
      </Container>
    )
  }

  if (error || !household) {
    return (
      <Container
        maxW="container.md"
        py={{ base: 4, md: 8 }}
        px={{ base: 2, md: 4 }}
      >
        <Card.Root>
          <Card.Body>
            <VStack gap={4}>
              <Heading size="lg">No Household Found</Heading>
              <Text>
                We couldn't find a household associated with your account.
                Please contact support for assistance.
              </Text>
              <Button onClick={() => navigate({ to: "/" })}>
                Return to Dashboard
              </Button>
            </VStack>
          </Card.Body>
        </Card.Root>
      </Container>
    )
  }

  return (
    <Container
      maxW="container.md"
      py={{ base: 4, md: 8 }}
      px={{ base: 2, md: 4 }}
    >
      <VStack gap={{ base: 4, md: 6 }} align="stretch">
        {/* Header */}
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "stretch", md: "center" }}
          gap={{ base: 3, md: 0 }}
        >
          <VStack align="start" gap={1}>
            <Heading size={{ base: "lg", md: "xl" }}>Household Profile</Heading>
            <Text color="gray.600" fontSize={{ base: "sm", md: "md" }}>
              Manage your family's information and emergency contacts
            </Text>
          </VStack>
          {!isEditing && (
            <Button
              colorPalette="blue"
              onClick={() => setIsEditing(true)}
              alignSelf={{ base: "stretch", md: "auto" }}
            >
              Edit Profile
            </Button>
          )}
        </Flex>

        {/* Profile Form */}
        <Card.Root>
          <Card.Header>
            <HStack justify="space-between">
              <Heading size="md">Family Information</Heading>
              {household.is_active ? (
                <Badge colorPalette="green">Active</Badge>
              ) : (
                <Badge colorPalette="red">Inactive</Badge>
              )}
            </HStack>
          </Card.Header>
          <Card.Body>
            <form onSubmit={form.handleSubmit(handleSave)}>
              <VStack gap={6} align="stretch">
                {/* Parent Information */}
                <Box>
                  <Heading size="sm" mb={4}>
                    Parent Information
                  </Heading>
                  <Stack gap={4}>
                    <Flex direction={{ base: "column", md: "row" }} gap={4}>
                      <Box flex={1}>
                        <Field
                          label="First Name"
                          required
                          invalid={!!form.formState.errors.parent_first_name}
                          errorText={
                            form.formState.errors.parent_first_name?.message
                          }
                        >
                          <Input
                            {...form.register("parent_first_name")}
                            disabled={!isEditing}
                          />
                        </Field>
                      </Box>
                      <Box flex={1}>
                        <Field
                          label="Last Name"
                          required
                          invalid={!!form.formState.errors.parent_last_name}
                          errorText={
                            form.formState.errors.parent_last_name?.message
                          }
                        >
                          <Input
                            {...form.register("parent_last_name")}
                            disabled={!isEditing}
                          />
                        </Field>
                      </Box>
                    </Flex>

                    <Field
                      label="Email Address"
                      required
                      invalid={!!form.formState.errors.email}
                      errorText={form.formState.errors.email?.message}
                    >
                      <Input
                        {...form.register("email")}
                        type="email"
                        disabled={!isEditing}
                      />
                    </Field>

                    <Flex direction={{ base: "column", md: "row" }} gap={4}>
                      <Box flex={1}>
                        <Field
                          label="Primary Phone"
                          invalid={!!form.formState.errors.phone}
                          errorText={form.formState.errors.phone?.message}
                        >
                          <Input
                            {...form.register("phone")}
                            type="tel"
                            placeholder="(555) 123-4567"
                            disabled={!isEditing}
                          />
                        </Field>
                      </Box>
                      <Box flex={1}>
                        <Field
                          label="Secondary Phone"
                          invalid={!!form.formState.errors.secondary_phone}
                          errorText={
                            form.formState.errors.secondary_phone?.message
                          }
                        >
                          <Input
                            {...form.register("secondary_phone")}
                            type="tel"
                            placeholder="(555) 987-6543"
                            disabled={!isEditing}
                          />
                        </Field>
                      </Box>
                    </Flex>

                    <Field
                      label="Home Address"
                      invalid={!!form.formState.errors.address}
                      errorText={form.formState.errors.address?.message}
                    >
                      <Textarea
                        {...form.register("address")}
                        placeholder="Street address, city, state, ZIP"
                        rows={3}
                        disabled={!isEditing}
                      />
                    </Field>
                  </Stack>
                </Box>

                <Separator />

                {/* Emergency Contact */}
                <Box>
                  <Heading size="sm" mb={4}>
                    Emergency Contact
                  </Heading>
                  <Stack gap={4}>
                    <Field
                      label="Emergency Contact Name"
                      invalid={!!form.formState.errors.emergency_contact_name}
                      errorText={
                        form.formState.errors.emergency_contact_name?.message
                      }
                    >
                      <Input
                        {...form.register("emergency_contact_name")}
                        placeholder="Full name"
                        disabled={!isEditing}
                      />
                    </Field>

                    <Flex direction={{ base: "column", md: "row" }} gap={4}>
                      <Box flex={1}>
                        <Field
                          label="Emergency Contact Phone"
                          invalid={
                            !!form.formState.errors.emergency_contact_phone
                          }
                          errorText={
                            form.formState.errors.emergency_contact_phone
                              ?.message
                          }
                        >
                          <Input
                            {...form.register("emergency_contact_phone")}
                            type="tel"
                            placeholder="(555) 123-4567"
                            disabled={!isEditing}
                          />
                        </Field>
                      </Box>
                      <Box flex={1}>
                        <Field
                          label="Relationship"
                          invalid={
                            !!form.formState.errors
                              .emergency_contact_relationship
                          }
                          errorText={
                            form.formState.errors.emergency_contact_relationship
                              ?.message
                          }
                        >
                          <Input
                            {...form.register("emergency_contact_relationship")}
                            placeholder="e.g., Grandparent, Aunt"
                            disabled={!isEditing}
                          />
                        </Field>
                      </Box>
                    </Flex>
                  </Stack>
                </Box>

                <Separator />

                {/* Medical Information */}
                <Box>
                  <Heading size="sm" mb={4}>
                    Medical Information
                  </Heading>
                  <Stack gap={4}>
                    <Flex direction={{ base: "column", md: "row" }} gap={4}>
                      <Box flex={1}>
                        <Field
                          label="Doctor First Name"
                          invalid={!!form.formState.errors.doctor_first_name}
                          errorText={
                            form.formState.errors.doctor_first_name?.message
                          }
                        >
                          <Input
                            {...form.register("doctor_first_name")}
                            disabled={!isEditing}
                          />
                        </Field>
                      </Box>
                      <Box flex={1}>
                        <Field
                          label="Doctor Last Name"
                          invalid={!!form.formState.errors.doctor_last_name}
                          errorText={
                            form.formState.errors.doctor_last_name?.message
                          }
                        >
                          <Input
                            {...form.register("doctor_last_name")}
                            disabled={!isEditing}
                          />
                        </Field>
                      </Box>
                    </Flex>

                    <Flex direction={{ base: "column", md: "row" }} gap={4}>
                      <Box flex={1}>
                        <Field
                          label="Doctor Phone"
                          invalid={!!form.formState.errors.doctor_phone}
                          errorText={
                            form.formState.errors.doctor_phone?.message
                          }
                        >
                          <Input
                            {...form.register("doctor_phone")}
                            type="tel"
                            placeholder="(555) 123-4567"
                            disabled={!isEditing}
                          />
                        </Field>
                      </Box>
                      <Box flex={1}>
                        <Field
                          label="Insurance Provider"
                          invalid={!!form.formState.errors.insurance_provider}
                          errorText={
                            form.formState.errors.insurance_provider?.message
                          }
                        >
                          <Input
                            {...form.register("insurance_provider")}
                            placeholder="e.g., Blue Cross"
                            disabled={!isEditing}
                          />
                        </Field>
                      </Box>
                    </Flex>
                  </Stack>
                </Box>

                <Separator />

                {/* Billing Preferences */}
                <Box>
                  <Heading size="sm" mb={4}>
                    Billing Preferences
                  </Heading>
                  <Stack gap={4}>
                    <Field
                      label="Billing Email (if different)"
                      invalid={!!form.formState.errors.billing_email}
                      errorText={form.formState.errors.billing_email?.message}
                      helperText="Leave blank to use your primary email"
                    >
                      <Input
                        {...form.register("billing_email")}
                        type="email"
                        placeholder="billing@example.com"
                        disabled={!isEditing}
                      />
                    </Field>

                    <Field
                      label="Enable Auto-Pay"
                      invalid={!!form.formState.errors.auto_pay_enabled}
                      errorText={
                        form.formState.errors.auto_pay_enabled?.message
                      }
                      helperText="Automatically charge your payment method on file"
                    >
                      <Controller
                        name="auto_pay_enabled"
                        control={form.control}
                        render={({ field }) => (
                          <Switch.Root
                            checked={!!field.value}
                            onCheckedChange={({ checked }) =>
                              field.onChange(checked)
                            }
                            disabled={!isEditing}
                          >
                            <Switch.HiddenInput ref={field.ref} />
                            <Switch.Control>
                              <Switch.Thumb />
                            </Switch.Control>
                            <Switch.Label>
                              {field.value ? "Enabled" : "Disabled"}
                            </Switch.Label>
                          </Switch.Root>
                        )}
                      />
                    </Field>
                  </Stack>
                </Box>

                <Separator />

                {/* Policy Agreements */}
                <Box>
                  <Heading size="sm" mb={4}>
                    Policy Agreements
                  </Heading>
                  <Stack gap={4}>
                    <Field
                      invalid={!!form.formState.errors.agreed_to_policy}
                      errorText={
                        form.formState.errors.agreed_to_policy?.message
                      }
                    >
                      <Controller
                        name="agreed_to_policy"
                        control={form.control}
                        render={({ field }) => (
                          <Switch.Root
                            checked={!!field.value}
                            onCheckedChange={({ checked }) =>
                              field.onChange(checked)
                            }
                            disabled={!isEditing}
                          >
                            <Switch.HiddenInput ref={field.ref} />
                            <Switch.Control>
                              <Switch.Thumb />
                            </Switch.Control>
                            <Switch.Label>
                              I agree to the school policies
                            </Switch.Label>
                          </Switch.Root>
                        )}
                      />
                    </Field>

                    <Field
                      invalid={!!form.formState.errors.agreed_to_hold_harmless}
                      errorText={
                        form.formState.errors.agreed_to_hold_harmless?.message
                      }
                    >
                      <Controller
                        name="agreed_to_hold_harmless"
                        control={form.control}
                        render={({ field }) => (
                          <Switch.Root
                            checked={!!field.value}
                            onCheckedChange={({ checked }) =>
                              field.onChange(checked)
                            }
                            disabled={!isEditing}
                          >
                            <Switch.HiddenInput ref={field.ref} />
                            <Switch.Control>
                              <Switch.Thumb />
                            </Switch.Control>
                            <Switch.Label>
                              I agree to the hold harmless agreement
                            </Switch.Label>
                          </Switch.Root>
                        )}
                      />
                    </Field>

                    {household.policy_agreement_date && (
                      <Text fontSize="sm" color="gray.600">
                        Agreed on:{" "}
                        {new Date(
                          household.policy_agreement_date,
                        ).toLocaleDateString()}
                      </Text>
                    )}
                  </Stack>
                </Box>

                {/* Action Buttons */}
                {isEditing && (
                  <>
                    <Separator />
                    <Flex
                      direction={{ base: "column-reverse", md: "row" }}
                      justify="flex-end"
                      gap={3}
                    >
                      <Button
                        variant="ghost"
                        onClick={handleCancel}
                        disabled={updateMutation.isPending}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        colorPalette="blue"
                        loading={updateMutation.isPending}
                        loadingText="Saving..."
                      >
                        Save Changes
                      </Button>
                    </Flex>
                  </>
                )}
              </VStack>
            </form>
          </Card.Body>
        </Card.Root>

        {/* Last Updated Info */}
        <Text fontSize="sm" color="gray.600" textAlign="center">
          Last updated: {new Date(household.updated_at).toLocaleString()}
        </Text>
      </VStack>
    </Container>
  )
}
